import { History } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useCatchUpFollowups } from "@/api/mutations";
import type { FollowupCatchUpResult } from "@/api/types";
import { Section } from "@/components/feedback/key-value";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { formatDateTime } from "@/lib/dates";
import { formatNumber } from "@/lib/formatters";

/**
 * Leads that went quiet before follow-ups existed: the rules don't revive them on their own (no
 * burst of texts on deploy). Preview, then plan their follow-ups, spread over each store's texting
 * hours at most N a day per store. Each one is still checked again when it's due.
 */
export function FollowupCatchUp({ organizationId, scope }: { organizationId: string | null; scope: string }) {
  const [days, setDays] = useState(90);
  const [perDay, setPerDay] = useState(20);
  const [preview, setPreview] = useState<FollowupCatchUpResult | null>(null);
  const run = useCatchUpFollowups();
  const valid = Number.isInteger(days) && days >= 1 && days <= 365 && Number.isInteger(perDay) && perDay >= 1 && perDay <= 500;
  const go = (dryRun: boolean) =>
    run.mutate(
      { organizationId, quietForAtMostDays: days, perStorePerDay: perDay, dryRun },
      {
        onSuccess: (r) => {
          setPreview(r);
          if (!r.dryRun)
            toast.success(`${formatNumber(r.followups)} follow-up(s) scheduled`, {
              description: r.firstAt && r.lastAt ? `From ${formatDateTime(r.firstAt)} to ${formatDateTime(r.lastAt)}` : undefined,
            });
        },
        onError: (err) => toast.error("Couldn't catch up the quiet leads", { description: err.message }),
      },
    );

  return (
    <Section title="Leads that went quiet before follow-ups">
      <p className="mb-3 text-muted-foreground text-xs">
        Rules only look at texts sent since follow-ups were turned on. Catch up {scope}'s older quiet leads: each gets the step its rule says, spread over the
        store's texting hours, most recently quiet first. They're checked again before sending (a reply, a booking or a closed lead cancels them).
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">Quiet for at most (days)</span>
          <Input
            type="number"
            aria-label="Quiet for at most (days)"
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value));
              setPreview(null);
            }}
            className="h-7 w-28"
          />
        </label>
        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">At most a day, per store</span>
          <Input
            type="number"
            aria-label="At most a day, per store"
            value={perDay}
            onChange={(e) => {
              setPerDay(Number(e.target.value));
              setPreview(null);
            }}
            className="h-7 w-28"
          />
        </label>
        <Button size="sm" variant="outline" disabled={!valid || run.isPending} onClick={() => go(true)}>
          {run.isPending && run.variables?.dryRun ? <Spinner data-icon="inline-start" /> : <History data-icon="inline-start" />}
          Preview
        </Button>
        {preview?.dryRun && preview.followups > 0 ? (
          <Button size="sm" disabled={!valid || run.isPending} onClick={() => go(false)}>
            {run.isPending && !run.variables?.dryRun ? <Spinner data-icon="inline-start" /> : null}
            Schedule {formatNumber(preview.followups)} follow-up(s)
          </Button>
        ) : null}
      </div>
      {!valid ? <p className="mt-2 text-destructive text-xs">Days: 1 to 365. Per store: 1 to 500 a day.</p> : null}
      {preview ? <CatchUpSummary result={preview} /> : null}
    </Section>
  );
}

function CatchUpSummary({ result: r }: { result: FollowupCatchUpResult }) {
  return (
    <div className="mt-3 space-y-2 text-xs" data-testid="catch-up-summary">
      <p>
        {r.dryRun ? "Would schedule" : "Scheduled"} <strong>{formatNumber(r.followups)}</strong> follow-up(s) for {formatNumber(r.leads)} quiet lead(s)
        {r.firstAt && r.lastAt ? `, from ${formatDateTime(r.firstAt)} to ${formatDateTime(r.lastAt)}` : ""}.
        {r.noRule ? ` ${formatNumber(r.noRule)} lead(s) match no rule.` : ""}
      </p>
      {r.truncated ? (
        <Alert>
          <AlertDescription>More quiet leads than one run handles: run it again afterwards for the rest.</AlertDescription>
        </Alert>
      ) : null}
      {r.byRule.length ? <p className="text-muted-foreground">By rule: {r.byRule.map((b) => `${b.name} (${formatNumber(b.count)})`).join(", ")}</p> : null}
      {r.stores.length ? (
        <table className="w-full">
          <thead className="text-[11px] text-muted-foreground uppercase">
            <tr className="border-b">
              <th className="py-1 text-left font-medium">Store</th>
              <th className="text-right font-medium">Follow-ups</th>
              <th className="text-right font-medium">From</th>
              <th className="text-right font-medium">To</th>
            </tr>
          </thead>
          <tbody>
            {r.stores.map((s) => (
              <tr key={s.store.id} className="border-b last:border-b-0">
                <td className="py-1">
                  {s.store.name}
                  {s.organization ? <span className="text-muted-foreground"> · {s.organization.name}</span> : null}
                </td>
                <td className="text-right tabular-nums">{formatNumber(s.followups)}</td>
                <td className="text-right">{formatDateTime(s.firstAt)}</td>
                <td className="text-right">{formatDateTime(s.lastAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
